import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./index.css";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

async function fetchAllSymbols() {
  const res = await fetch(`${BASE}/api/symbols/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Failed to fetch symbols");
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

const FILTERS = [
  { id: "all",     label: "All Symbols" },
  { id: "shariah", label: "Shariah Compliant" },
  { id: "active",  label: "Active" },
];

const PAGE = 60;

// flexible field readers (list API field names vary)
const getPrice  = (s) => s.last_price ?? s.price ?? s.close_price ?? s.close ?? null;
const getChange = (s) => s.change_percent ?? s.change_pct ?? s.changePercent ?? null;
const getVolume = (s) => s.volume ?? s.total_volume ?? s.volume_today ?? null;

const fmtPrice = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtVol   = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString());

const SymLogo = ({ code }) => (
  <div className="sym-logo">
    <img
      src={`https://api.orbitsecurities.com.pk/images/symbols/${code}.svg`}
      alt={code}
      onError={(e) => {
        e.target.style.display = "none";
        e.target.parentNode.classList.add("fallback");
        e.target.parentNode.textContent = code?.slice(0, 3);
      }}
    />
  </div>
);

const Symbols = () => {
  const history = useHistory();
  const location = useLocation();
  const initialFilter = new URLSearchParams(location.search).get("filter") || "all";

  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState("");
  const [filter, setFilter]   = useState(initialFilter);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    fetchAllSymbols()
      .then(setSymbols)
      .catch(() => setSymbols([]))
      .finally(() => setLoading(false));
  }, []);

  // keep filter in sync if the query param changes (e.g. navigating from a card)
  useEffect(() => {
    const f = new URLSearchParams(location.search).get("filter") || "all";
    setFilter(f);
  }, [location.search]);

  const selectFilter = (id) => {
    setFilter(id);
    setVisible(PAGE);
    history.replace(id === "all" ? "/symbols" : `/symbols?filter=${id}`);
  };

  const filtered = useMemo(() => {
    let list = symbols;
    if (filter === "shariah") list = list.filter(s => s.is_shariah);
    else if (filter === "active") list = list.filter(s => s.is_active);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter(s => s.code?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q));
    return list;
  }, [symbols, filter, query]);

  const shown = filtered.slice(0, visible);

  return (
    <div className="sym-page">
      <div className="sym-wrap">

        {/* header */}
        <div className="sym-head">
          <div>
            <h1 className="sym-title">PSX Symbols</h1>
            <p className="sym-sub">Browse and search all listed companies on the Pakistan Stock Exchange.</p>
          </div>
          <div className="sym-search">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search symbol or company…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setVisible(PAGE); }}
            />
          </div>
        </div>

        {/* filter chips */}
        <div className="sym-filters">
          {FILTERS.map(f => (
            <button key={f.id} className={`sym-chip${filter === f.id ? " active" : ""}`} onClick={() => selectFilter(f.id)}>
              {f.label}
            </button>
          ))}
          {!loading && <span className="sym-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>}
        </div>

        {/* table */}
        {loading ? (
          <div className="sym-muted">Loading symbols…</div>
        ) : filtered.length === 0 ? (
          <div className="sym-muted">No symbols match your filter.</div>
        ) : (
          <>
            <div className="sym-table-wrap">
              <table className="sym-table">
                <thead>
                  <tr>
                    <th className="l">Symbol</th>
                    <th className="l">Name</th>
                    <th className="r">Price</th>
                    <th className="r">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(s => {
                    const chg = getChange(s);
                    const chgNum = chg != null ? parseFloat(chg) : null;
                    return (
                      <tr key={s.code} onClick={() => history.push(`/trading-dashboard/${s.code}`)}>
                        <td>
                          <div className="sym-cell">
                            <SymLogo code={s.code} />
                            <div className="sym-cell-id">
                              <span className="sym-cell-code">{s.code}</span>
                              <span className="sym-cell-ex">
                                PSX
                                {s.is_shariah && <span className="sym-tag shariah">✓ Shariah</span>}
                                {s.is_active === false && <span className="sym-tag inactive">Inactive</span>}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="sym-td-name">{s.name || "—"}</td>
                        <td className="r">
                          <div className="sym-price">{fmtPrice(getPrice(s))}</div>
                          {chgNum != null && (
                            <div className={`sym-chg ${chgNum >= 0 ? "up" : "down"}`}>
                              {chgNum >= 0 ? "▲" : "▼"} {Math.abs(chgNum).toFixed(2)}%
                            </div>
                          )}
                        </td>
                        <td className="r sym-vol">{fmtVol(getVolume(s))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {visible < filtered.length && (
              <div className="sym-more-wrap">
                <button className="sym-more" onClick={() => setVisible(v => v + PAGE)}>
                  Load more ({filtered.length - visible} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Symbols;
